import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Channel, Socket } from "phoenix";
import {
  AIAssistantApiError,
  getUniqueId,
  usePrevious,
  useToast,
  useTranslations,
} from "@jugl-web/utils";
import { isEqual } from "lodash";
import { useTaskBoardList } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardList";
import removeMarkdown from "remove-markdown";
import { marked } from "marked";
import { useUserGeneralProfile } from "@jugl-web/domain-resources/users/hooks/useUserGeneralProfile";
import { useTaskObjectNaming } from "@jugl-web/domain-resources/tasks/hooks/useTaskObjectNaming";
import { AIAssistantContext } from "./AIAssistantContext";
import {
  AIAgent,
  AIAgentType,
  AIAssistantMessage,
  AIQuota,
  AIResponseStream,
  AIUserPerformanceDialogState,
  PhxResponse,
} from "../types";
import { AIUserPerformanceDialog } from "../AIUserPerformanceDialog";
import { AIAssistantDialog } from "../AIAssistantDialog";
import { AIPromptSuggestions } from "../consts";

interface AIAssistantProviderProps {
  entityId: string;
  children: React.ReactNode;
  socket?: Socket;
  userId?: string;
}

export const AIAssistantProvider = ({
  children,
  socket,
  userId,
  entityId,
}: AIAssistantProviderProps) => {
  const { t } = useTranslations();
  const { toast } = useToast();

  const [isAIAssistantDialogOpen, setIsAIAssistantDialogOpen] = useState(false);
  const [agent, setAgent] = useState<AIAgent>();
  const [messages, setMessages] = useState<AIAssistantMessage[]>([]);
  const [isAILimitReached, setIsAILimitReached] = useState(false);
  const [isDataCopied, setIsDataCopied] = useState(false);
  const [userPerformanceDialogState, setUserPerformanceDialogState] =
    useState<AIUserPerformanceDialogState>();
  const [isChannelError, setIsChannelError] = useState(false);
  const [isError, setIsError] = useState(false);

  const messageTimeoutRef = useRef<NodeJS.Timeout>();
  const channelRef = useRef<Channel>();
  const cleanupRef = useRef<() => void>();

  const prevAgent = usePrevious(agent);

  const { accessibleBoards: boards } = useTaskBoardList({
    entityId,
    meId: userId || "",
  });

  const { profile: user } = useUserGeneralProfile({
    entityId,
    userId,
  });

  const taskObjectNaming = useTaskObjectNaming({
    entityId,
    boardId: agent?.agent === AIAgentType.board ? agent.board_id : null,
  });

  const getChangeAgentMessage = useCallback((): Pick<
    AIAssistantMessage,
    "content" | "suggestions"
  > => {
    if (agent?.agent === "help") {
      return {
        content: `###Hello ${
          user?.displayName || ""
        }\nHere is some articles that might help you with Onboarding📚 Let me know if you have any other questions!🤝`,
        suggestions: AIPromptSuggestions[agent.agent],
      };
    }

    return {
      content: `###Hello ${
        user?.displayName || ""
      }\nHow can i help you with the Board *${
        agent?.agent === "my_tasks"
          ? "My tasks"
          : boards.find((board) => board.id === agent?.board_id)?.name || ""
      }*? 🤓`,
      suggestions: agent?.agent
        ? AIPromptSuggestions[agent.agent].map((suggestion) =>
            suggestion.replace("tasks", taskObjectNaming.plural)
          )
        : [],
    };
  }, [agent, user?.displayName, boards, taskObjectNaming.plural]);
  const handleOpenAIAssistantDialog = useCallback(
    (isOpen: boolean, boardId?: string) => {
      if (!isOpen) {
        setIsAIAssistantDialogOpen(isOpen);
        return;
      }

      if (!entityId || !user) return;
      if (!boardId) {
        setAgent({ agent: AIAgentType.help });
      } else if (boardId === "my" || boardId === "team") {
        setAgent({
          agent: AIAgentType.myTasks,
          entity_id: entityId,
          user_id: user.id,
        });
      } else {
        setAgent({
          agent: AIAgentType.board,
          entity_id: entityId,
          board_id: boardId,
        });
      }
      setUserPerformanceDialogState(undefined);
      setIsAIAssistantDialogOpen(isOpen);
    },
    [entityId, user]
  );

  useEffect(() => {
    if (agent && prevAgent && !isEqual(agent, prevAgent)) {
      setMessages((prev) => [
        ...prev,
        {
          id: getUniqueId(),
          isOutgoing: false,
          ...getChangeAgentMessage(),
        },
      ]);
    }
  }, [agent, getChangeAgentMessage, prevAgent, taskObjectNaming.plural]);

  useEffect(() => {
    setMessages([]);
    setIsAIAssistantDialogOpen(false);
    setAgent(undefined);
    setUserPerformanceDialogState(undefined);
    setIsAILimitReached(false);
    setIsDataCopied(false);
    setIsChannelError(false);
    setIsError(false);
    clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = undefined;
  }, [entityId]);

  const handleCopyData = useCallback(
    async (data: string) => {
      const html = await marked(data);

      navigator.clipboard
        .write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([removeMarkdown(data)], {
              type: "text/plain",
            }),
          }),
        ])
        .then(() => {
          toast(
            t({
              id: "ai-assistant.text-was-copied-to-clipboard",
              defaultMessage: "Text was copied to clipboard",
            })
          );
          setIsDataCopied(true);
          setTimeout(() => {
            setIsDataCopied(false);
          }, 5000);
        });
    },
    [toast, t]
  );

  const handleIncomingChunk = useCallback((response: AIResponseStream) => {
    clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = undefined;

    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && !last.isOutgoing) {
        const updated = {
          ...last,
          content: last.content + response.chunk,
        };
        return [...prev.slice(0, -1), updated];
      }

      return [
        ...prev,
        {
          id: getUniqueId(),
          content: response.chunk,
          isOutgoing: false,
        },
      ];
    });
  }, []);

  const handleAIQuota = useCallback((response: AIQuota) => {
    clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = undefined;

    setIsAILimitReached(response.remaining === 0);
  }, []);

  const handlePhxReply = useCallback(
    (reply: PhxResponse<AIAssistantApiError>) => {
      clearTimeout(messageTimeoutRef.current);
      messageTimeoutRef.current = undefined;

      if (
        reply.status === "error" &&
        reply.response.error === "quota_limit_exceeded"
      ) {
        setIsAILimitReached(true);
      }
    },
    []
  );

  const joinChannel = useCallback(
    (socketParam: Socket, agentParam: AIAgent) => {
      const channel = socketParam.channel("ai:lobby", {
        ...agentParam,
      });

      channel
        .join()
        .receive("ok", () => {
          channelRef.current = channel;
          setIsChannelError(false);
        })
        .receive("error", () => {
          setIsChannelError(true);
        });

      const aiQuotaRefId = channel.on("ai_quota", handleAIQuota);
      const phxReplyRefId = channel.on("phx_reply", handlePhxReply);
      const responseStreamRefId = channel.on(
        "response_stream",
        handleIncomingChunk
      );

      return () => {
        channel.off("response_stream", responseStreamRefId);
        channel.off("ai_quota", aiQuotaRefId);
        channel.off("phx_reply", phxReplyRefId);
        channel.leave();
      };
    },
    [handleAIQuota, handleIncomingChunk, handlePhxReply]
  );

  const reconnectChannel = useCallback(() => {
    if (!socket || !agent) return;

    cleanupRef.current?.();

    cleanupRef.current = joinChannel(socket, agent);
  }, [socket, agent, joinChannel]);

  useEffect(() => {
    reconnectChannel();

    return () => {
      cleanupRef.current?.();
    };
  }, [reconnectChannel]);

  const sendMessage = useCallback((prompt: string) => {
    if (!channelRef.current) return;

    setIsError(false);

    setMessages((prev) => [
      ...prev,
      {
        id: getUniqueId(),
        content: prompt,
        isOutgoing: true,
      },
    ]);

    channelRef.current.push("send", { prompt });

    clearTimeout(messageTimeoutRef.current);
    messageTimeoutRef.current = setTimeout(() => setIsError(true), 60000);
  }, []);

  const resendMessage = useCallback(() => {
    const lastMessage = [...messages].reverse().find((msg) => msg.isOutgoing);
    if (!lastMessage) return;

    sendMessage(lastMessage.content);
  }, [messages, sendMessage]);

  const handleOpenUserPerformanceDialog = useCallback(
    (newState: AIUserPerformanceDialogState) => {
      setIsAIAssistantDialogOpen(false);
      setUserPerformanceDialogState(newState);
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      messages,
      user: user || undefined,
      agent,
      entityId,
      isAILimitReached,
      isDataCopied,
      isAIAssistantDialogOpen,
      isChannelError,
      isError,
      sendMessage,
      handleOpenAIAssistantDialog,
      changeAgent: setAgent,
      copyData: handleCopyData,
      openUserPerformanceDialog: handleOpenUserPerformanceDialog,
      reconnectChannel,
      resendMessage,
    }),
    [
      messages,
      user,
      agent,
      entityId,
      isAILimitReached,
      isAIAssistantDialogOpen,
      isDataCopied,
      isChannelError,
      isError,
      handleCopyData,
      sendMessage,
      handleOpenAIAssistantDialog,
      handleOpenUserPerformanceDialog,
      reconnectChannel,
      resendMessage,
    ]
  );

  return (
    <AIAssistantContext.Provider value={contextValue}>
      {children}
      <AIAssistantDialog />
      {userPerformanceDialogState && (
        <AIUserPerformanceDialog
          {...userPerformanceDialogState}
          onClose={() => setUserPerformanceDialogState(undefined)}
        />
      )}
    </AIAssistantContext.Provider>
  );
};
